/* Country / currency dropdown
 *
 * The search box and arrow keys for the dropdown in snippets/localization-selector.liquid. Prestige's <x-popover>
 * opens and closes it and its <x-listbox> marks the rows; this only filters the rows as the customer types and
 * walks the visible ones with the arrow keys, since the listbox's own arrow handling steps onto hidden siblings
 * and stalls.
 *
 * Markup, for reference:
 *   <localization-selector>
 *     > .localization-popover__search > input
 *     > .localization-popover__group > .localization-popover__label + x-listbox > button[role=option][data-search]
 *     > .localization-popover__empty[hidden]
 */

class LocalizationSelector extends HTMLElement {
  #listening = false;

  connectedCallback() {
    // On phones Prestige lifts the popover out of the header and re-attaches it to <body>, which runs this again.
    if (this.#listening) return;
    this.#listening = true;

    this.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeyDown, true);

    const popover = this.closest('x-popover');
    if (popover) {
      popover.addEventListener('dialog:after-show', this.#onShow);
      popover.addEventListener('dialog:after-hide', this.#reset);
    }
  }

  get #input() {
    return this.querySelector('.localization-popover__search-input');
  }

  get #options() {
    return Array.from(this.querySelectorAll('[role="option"]'));
  }

  get #visibleOptions() {
    return this.#options.filter((option) => !option.hidden);
  }

  #onInput = (event) => {
    if (event.target !== this.#input) return;
    this.#filter(event.target.value);
  };

  #onShow = () => {
    // On phones the sheet's title bar sticks at the top of the same scroll box, so the search sticks under it.
    const header = this.closest('x-popover')?.shadowRoot?.querySelector('[part~="header"]');
    const height = header ? header.getBoundingClientRect().height : 0;
    this.style.setProperty('--localization-header-height', `${Math.round(height)}px`);
  };

  #reset = () => {
    const input = this.#input;
    if (!input) return;
    input.value = '';
    this.#filter('');
  };

  #filter(query) {
    const needle = normalise(query);

    this.#options.forEach((option) => {
      option.hidden = needle !== '' && !normalise(option.dataset.search || option.textContent).includes(needle);
    });

    this.querySelectorAll('.localization-popover__group').forEach((group) => {
      group.hidden = !group.querySelector('[role="option"]:not([hidden])');
    });

    const empty = this.querySelector('.localization-popover__empty');
    if (empty) empty.hidden = this.#visibleOptions.length > 0;
  }

  #onKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const visible = this.#visibleOptions;
    if (visible.length === 0) return;

    const index = visible.indexOf(event.target);
    let next;

    if (index === -1) {
      // From the search box (or anywhere else) the arrows land on the first or last row.
      next = event.key === 'ArrowDown' ? visible[0] : visible[visible.length - 1];
    } else {
      next = visible[index + (event.key === 'ArrowDown' ? 1 : -1)];
      // Arrow up off the top returns to the search box when there is one.
      if (!next && event.key === 'ArrowUp' && this.#input) next = this.#input;
    }

    if (!next) return;

    event.preventDefault();
    event.stopPropagation();
    next.focus();
  };
}

// "Cote d'Ivoire" should find Côte d'Ivoire, and "curacao" Curaçao.
function normalise(text) {
  return String(text)
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

if (!window.customElements.get('localization-selector')) {
  window.customElements.define('localization-selector', LocalizationSelector);
}
